import { useState, useEffect } from "react"
import { DetailPage, Row } from "../../components/crud/DetailPage"
import { FkLink, ListPage } from "../../components/crud/ListPage"
import { AsyncSelect, Checkbox, MoneyInput, NumberInput, Select, TextInput } from "../../components/forms/Inputs"
import { useOptions } from "../../hooks/useOptions"
import { useTranslations } from "../../layers/Localization"
import { useApi } from "../../hooks/useApi"
import { Currency, roundCurrency, toCurrency } from "../../hooks/helpers"
import { useRegimeTvaOptions } from "./Clients"
import { useParams } from "react-router-dom"
import { useCodesTvaOptions } from "./Articles"

export const getTotalsFactureDetail = (item, codesTvaOptions) => {
    const codeTva = codesTvaOptions.find(x => x.value === item.codeTva)
    const totalHtva = item.quantite !== null && item.prixUnitaire !== null && codeTva ? roundCurrency(item.quantite * item.prixUnitaire) : null
    const totalTva = item.quantite !== null && item.prixUnitaire !== null && codeTva ? roundCurrency(item.quantite * item.prixUnitaire * (codeTva.taux / 100)) : null
    const totalTtc = item.quantite !== null && item.prixUnitaire !== null && codeTva ? roundCurrency(totalHtva + totalTva) : null
    return { totalHtva, totalTva, totalTtc }
}

function FacturesDetailFields({ item, set, tab }) {
    const codesTvaOptions = useCodesTvaOptions()
    const [facture, setFacture] = useState()

    const t = useTranslations()
    const api = useApi()
    const { id } = useParams()
    const articleOptions = useOptions('articles', 'nom')
    const chantiersOptions = useOptions('chantiers', ['nom', 'descriptionFacture'], { query: facture ? `clientId=${facture?.clientId}` : '' })
    const travauxPonctuelsOptions = useOptions('travauxPonctuels', ['nom', 'date', 'prixVente'], { query: facture ? `clientId=${facture?.clientId}&realise=true` : '' })
    const regimeTvaOptions = useRegimeTvaOptions()

    useEffect(() => {
        if (id === 'new' && item.factureId) api.get(`/facturesDetail?factureId=${item.factureId}`).then(factureDetails => {
            if (!item.ordre) set('ordre')(Math.max(...factureDetails.map(x => x.ordre), 0) + 1)
        })
    }, [id, item.factureId])
    useEffect(() => {
        if (item.factureId) api.get(`/factures/${item.factureId}`).then(setFacture)
    }, [item.factureId])
    useEffect(() => {
        if (!item.codeTva && facture?.clientRegimeTva) {
            set('codeTva')(regimeTvaOptions.find(x => x.value === facture?.clientRegimeTva).codeTva)
        }
    }, [facture?.clientRegimeTva])

    const changeChantier = (id, chantier) => {
        set('chantierId')(id)
        if (id) {
            if (item.articleId) set('articleId')(null)
            if (item.travailPonctuelId) set('travailPonctuelId')(null)
            set('prixUnitaire')(chantier.prixVente)
            set('nom')(chantier.descriptionFacture.toUpperCase())
        } else if (!item.articleId && !item.travailPonctuelId) {
            set('prixUnitaire')(null)
            set('nom')(null)
        }
    }
    const changeArticle = (id, article) => {
        set('articleId')(id)
        if (id) {
            if (item.chantierId) set('chantierId')(null)
            if (item.travailPonctuelId) set('travailPonctuelId')(null)
            set('prixUnitaire')(article.prixVente)
            set('nom')(article.nom.toUpperCase())
        } else if (!item.chantierId && !item.travailPonctuelId) {
            set('prixUnitaire')(null)
            set('nom')(null)
        }
    }
    const changeTravailPonctuel = (id, travailPonctuel) => {
        set('travailPonctuelId')(id)
        if (id) {
            if (item.chantierId) set('chantierId')(null)
            if (item.articleId) set('articleId')(null)
            set('prixUnitaire')(travailPonctuel.prixVente)
            set('nom')(travailPonctuel.nom.toUpperCase())
        } else if (!item.chantierId && !item.articleId) {
            set('prixUnitaire')(null)
            set('nom')(null)
        }
    }
    const onNomChange = value => {
        set('nom')(value.toUpperCase())
    }

    return <>
        <Row>
            <NumberInput label={t("Ordre")} value={item.ordre} onChange={set('ordre')} required disabled={facture?.comptabilite} />
            <AsyncSelect className="grow" label={t("Chercher chantier")} value={item.chantierId} onChange={changeChantier} options={chantiersOptions} disabled={facture?.comptabilite} />
            <AsyncSelect className="grow" label={t("Chercher article")} value={item.articleId} onChange={changeArticle} options={articleOptions} disabled={facture?.comptabilite} />
            <AsyncSelect className="grow" label={t("Chercher travail ponctuel")} value={item.travailPonctuelId} onChange={changeTravailPonctuel} options={travauxPonctuelsOptions} disabled={facture?.comptabilite} />
        </Row>
        <hr />
        <TextInput label={t("Désignation")} value={item.nom} onChange={onNomChange} required disabled={facture?.comptabilite} />
        <Row>
            <MoneyInput label={t("Prix unitaire")} value={item.prixUnitaire} onChange={set('prixUnitaire')} required min={item.exportPopsy ? "0.01" : "0"} disabled={facture?.comptabilite} />
            <NumberInput label={t("Quantité")} value={item.quantite} onChange={set('quantite')} required min={item.exportPopsy ? "1" : "0"} step="0.001" disabled={facture?.comptabilite} />
            <Select label={t("Code TVA")} value={item.codeTva} onChange={set('codeTva')} options={codesTvaOptions} required disabled={facture?.comptabilite} />
        </Row>
        <Checkbox label={t("Export Popsy")} value={item.exportPopsy} onChange={set('exportPopsy')} disabled={facture?.comptabilite} />
        <hr />
        <div className="flex">
            <div className="font-bold grid grid-cols-2 gap-x-8 gap-y-3">
                <div>{t("Total HTVA")}:</div>
                <div>{toCurrency(getTotalsFactureDetail(item, codesTvaOptions).totalHtva || 0)}</div>
                <div>{t("Total TVA")}:</div>
                <div>{toCurrency(getTotalsFactureDetail(item, codesTvaOptions).totalTva || 0)}</div>
                <div>{t("Total TTC")}:</div>
                <div>{toCurrency(getTotalsFactureDetail(item, codesTvaOptions).totalTtc || 0)}</div>
            </div>
        </div>
    </>
}

export function FacturesDetailsList(props) {
    const codesTvaOptions = useCodesTvaOptions()
    const t = useTranslations()

    return <ListPage model="facturesDetail" title={t("Détails")} columns={{
        [t('Ordre')]: 'ordre',
        [t('Désignation')]: 'nom',
        [t('Quantité')]: 'quantite',
        [t('Prix unitaire')]: { sort: 'prixUnitaire', value: item => <Currency value={item.prixUnitaire} /> },
        [t('Total HTVA')]: { value: item => <Currency value={getTotalsFactureDetail(item, codesTvaOptions).totalHtva} /> },
        [t('Code TVA')]: { sort: 'codeTva', value: item => <FkLink>{codesTvaOptions.find(x => x.value === item.codeTva).label}</FkLink> },
        [t('Total TTC')]: { value: item => <Currency value={getTotalsFactureDetail(item, codesTvaOptions).totalTtc} /> },
    }} {...props} />
}

export function FacturesDetailDetail(props) {
    const t = useTranslations()
    return <DetailPage model="facturesDetail" title={t("Factures Detail")} Fields={FacturesDetailFields} defaults={{
        exportPopsy: true,
        quantite: 1,
    }} {...props} />
}
